import { createFileRoute, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'

export const Route = createFileRoute('/unsubscribe')({
  validateSearch: z.object({ token: z.string().optional() }),
  head: () => ({ meta: [{ title: 'Unsubscribe, HOWDEN' }] }),
  component: UnsubscribePage,
})

type Status = 'loading' | 'valid' | 'already' | 'invalid' | 'submitting' | 'done' | 'error'

function UnsubscribePage() {
  const { token } = useSearch({ from: '/unsubscribe' })
  const [status, setStatus] = useState<Status>('loading')

  useEffect(() => {
    if (!token) {
      setStatus('invalid')
      return
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.valid) setStatus('valid')
        else if (data.reason === 'already_unsubscribed') setStatus('already')
        else setStatus('invalid')
      })
      .catch(() => setStatus('error'))
  }, [token])

  async function confirm() {
    if (!token) return
    setStatus('submitting')
    try {
      const r = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await r.json()
      if (data.success) setStatus('done')
      else if (data.reason === 'already_unsubscribed') setStatus('already')
      else setStatus('error')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="mx-auto max-w-md px-6 py-24 text-center lg:px-12">
      <p className="text-xs uppercase tracking-luxe text-muted-foreground">
        Email Preferences
      </p>
      <h1 className="mt-4 font-serif text-4xl font-light">Unsubscribe</h1>

      {status === 'loading' && (
        <p className="mt-8 text-sm text-muted-foreground">Verifying…</p>
      )}
      {status === 'invalid' && (
        <p className="mt-8 text-sm text-muted-foreground">
          This unsubscribe link is invalid or has expired.
        </p>
      )}
      {status === 'already' && (
        <p className="mt-8 text-sm text-muted-foreground">
          You have already unsubscribed. No further emails will be sent.
        </p>
      )}
      {status === 'error' && (
        <p className="mt-8 text-sm text-muted-foreground">
          Something interrupted the request. Please try again.
        </p>
      )}
      {(status === 'valid' || status === 'submitting') && (
        <>
          <p className="mt-8 text-sm text-muted-foreground">
            Confirm to stop receiving emails from HOWDEN.
          </p>
          <button
            onClick={confirm}
            disabled={status === 'submitting'}
            className="mt-8 w-full border border-foreground bg-foreground px-6 py-4 text-xs uppercase tracking-luxe text-background transition-opacity hover:opacity-80 disabled:opacity-50"
          >
            {status === 'submitting' ? 'Unsubscribing…' : 'Confirm unsubscribe'}
          </button>
        </>
      )}
      {status === 'done' && (
        <p className="mt-8 text-sm text-muted-foreground">
          You have been unsubscribed. We will not contact you again.
        </p>
      )}
    </div>
  )
}
